import { TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Line, LineChart, ResponsiveContainer, Tooltip } from 'recharts';

import { BehaviouralFlagBanner } from '../components/domain/BehaviouralFlagBanner';
import { BehaviouralFlagFeed } from '../components/domain/BehaviouralFlagFeed';
import { DrawdownChart } from '../components/domain/DrawdownChart';
import { DrawdownGauge } from '../components/domain/DrawdownGauge';
import { KeyMetricsStrip } from '../components/domain/KeyMetricsStrip';
import { MarketStanceCard } from '../components/domain/MarketStanceCard';
import { MetricCard } from '../components/domain/MetricCard';
import { PnlChart } from '../components/domain/PnlChart';
import { PortfolioDisciplineCard } from '../components/domain/PortfolioDisciplineCard';
import { PricesAsOf } from '../components/domain/PricesAsOf';
import { TopStatusBar } from '../components/domain/TopStatusBar';
import { TradingPortfolioHeatCard } from '../components/domain/TradingPortfolioHeatCard';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { usePortfolioSummary, usePrimaryAccount } from '../features/accounts/queries';
import {
  useDrawdownHistory,
  usePnlHistory,
  usePortfolioMetrics,
} from '../features/analytics/queries';
import { useMe } from '../features/auth/queries';
import { usePositions, useTradingPortfolioHeat } from '../features/positions/queries';
import { PendingReviewBanner } from '../features/reviews/PendingReviewBanner';
import { useCurrentNavSnapshot, useLatestSnapshot } from '../features/snapshots/queries';
import type { PortfolioSummaryDto, PortfolioSummaryPositionDto } from '../lib/api/endpoints';
import { formatCurrency } from '../lib/format/currency';
import { relativeTimestamp } from '../lib/format/date';

function daysAgoIso(days: number): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
}

function snapshotAgeDays(asOfDate: string): number {
  const snapshotDate = new Date(`${asOfDate}T00:00:00.000Z`);
  const now = new Date();
  const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const ageMs = todayUtc - snapshotDate.getTime();
  return Math.max(0, Math.floor(ageMs / 86_400_000));
}

function formatPercent(value: string | number | null | undefined, digits = 1): string {
  if (value === null || value === undefined) return '-';
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return '-';
  return `${numeric.toFixed(digits)}%`;
}

function signedFormatCurrency(value: string | number | null | undefined, currency: string): string {
  if (value === null || value === undefined) return '-';
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return '-';
  const formatted = formatCurrency(numeric, currency);
  return numeric > 0 ? `+${formatted}` : formatted;
}

function pnlTone(value: string | number | null | undefined): 'danger' | 'neutral' | 'success' {
  const numeric = typeof value === 'number' ? value : Number(value ?? 0);
  if (numeric > 0) return 'success';
  if (numeric < 0) return 'danger';
  return 'neutral';
}

function formatRMultiple(position: PortfolioSummaryPositionDto): string {
  const numeric = Number(position.rMultiple);
  if (!Number.isFinite(numeric)) return '-';
  const sign = numeric > 0 ? '+' : '';
  return `${sign}${numeric.toFixed(1)}R`;
}

function PortfolioSummarySkeleton() {
  return (
    <section className="grid gap-4 md:grid-cols-2" aria-label="Portfolio health loading">
      {['pnl', 'best', 'nav'].map((key) => (
        <Card key={key} className="min-h-40">
          <div className="space-y-4" aria-hidden="true">
            <div className="h-4 w-32 animate-pulse rounded bg-bg-muted" />
            <div className="h-8 w-40 animate-pulse rounded bg-bg-muted" />
            <div className="h-3 w-24 animate-pulse rounded bg-bg-muted" />
          </div>
        </Card>
      ))}
    </section>
  );
}

function PortfolioSummaryWidgets({
  currency,
  summary,
}: {
  currency: string;
  summary: PortfolioSummaryDto;
}) {
  const navData = summary.navHistory.map((point) => ({
    date: point.date,
    nav: Number(point.nav),
  }));
  const firstNav = navData[0]?.nav ?? 0;
  const latestNav = navData.at(-1)?.nav ?? firstNav;
  const navLineColor = latestNav >= firstNav ? '#10B981' : '#EF4444';
  const bestPosition = summary.bestPosition;

  return (
    <section className="grid gap-4 md:grid-cols-2" aria-label="Portfolio health">
      <Card>
        <div className="text-sm font-medium text-text-muted">Open Book P&amp;L</div>
        <Badge className="mt-3" size="lg" tone={pnlTone(summary.totalUnrealizedPnlDollar)}>
          {signedFormatCurrency(summary.totalUnrealizedPnlDollar, currency)}
        </Badge>
        <div className="mt-1">
          <Badge tone={pnlTone(summary.totalUnrealizedPnlPct)}>
            {formatPercent(summary.totalUnrealizedPnlPct)}
          </Badge>
        </div>
        <div className="mt-4 text-sm text-text-muted">
          across {summary.openPositionCount} positions
        </div>
      </Card>

      <Card>
        <div className="text-sm font-medium text-text-muted">Best Position</div>
        {bestPosition ? (
          <>
            <Link
              to={`/positions/${bestPosition.symbol}`}
              className="mt-3 inline-block text-2xl font-semibold text-primary hover:underline"
            >
              {bestPosition.symbol}
            </Link>
            <div className="mt-2 text-lg font-semibold tabular-nums text-success">
              {formatRMultiple(bestPosition)} 🔒
            </div>
            <div className="mt-1">
              <Badge tone={pnlTone(bestPosition.unrealizedPnlPct)}>
                {formatPercent(bestPosition.unrealizedPnlPct)}
              </Badge>
            </div>
          </>
        ) : (
          <p className="mt-3 text-sm text-text-muted">No position with an R multiple yet.</p>
        )}
      </Card>

      <Card>
        <div className="text-sm font-medium text-text-muted">NAV History</div>
        {navData.length >= 2 ? (
          <div className="mt-4 h-24">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={navData} margin={{ top: 6, right: 4, bottom: 0, left: 4 }}>
                <Tooltip
                  formatter={(value) => [formatCurrency(Number(value), currency), 'NAV']}
                  labelFormatter={(label) => String(label)}
                  contentStyle={{
                    backgroundColor: '#222837',
                    border: '1px solid #2A3142',
                    borderRadius: '4px',
                    color: '#E6E9EF',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="nav"
                  stroke={navLineColor}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="mt-3 text-sm text-text-muted">Not enough data</p>
        )}
      </Card>
    </section>
  );
}

export function Dashboard() {
  const me = useMe();
  const { accounts, account: primaryAccount } = usePrimaryAccount();
  const portfolioSummary = usePortfolioSummary(primaryAccount?.id ?? null);
  const latestSnapshot = useLatestSnapshot(primaryAccount?.id ?? null);
  const currentNavSnapshot = useCurrentNavSnapshot(Boolean(primaryAccount));
  const metrics = usePortfolioMetrics(primaryAccount?.id ?? null);
  const tradingPortfolioHeat = useTradingPortfolioHeat(
    primaryAccount?.id ?? null,
    primaryAccount?.mandate === 'SWING_TRADING',
  );
  const drawdownHistory = useDrawdownHistory(
    primaryAccount?.id ?? null,
    daysAgoIso(90),
    daysAgoIso(0),
  );
  const [pnlGroupBy, setPnlGroupBy] = useState<'day' | 'week' | 'month'>('week');
  const pnlHistory = usePnlHistory(
    primaryAccount?.id ?? null,
    daysAgoIso(30),
    daysAgoIso(0),
    pnlGroupBy,
  );
  const positions = usePositions(primaryAccount?.id ?? null);
  const accountCurrency = primaryAccount?.currency ?? me.data?.baseCurrency ?? 'USD';
  const topPositions = (positions.data?.positions ?? [])
    .slice()
    .sort((left, right) => Number(right.pctNav) - Number(left.pctNav))
    .slice(0, 5);
  const displayedSnapshot = currentNavSnapshot.data ?? latestSnapshot.data;
  const displayedCurrency = displayedSnapshot?.currency ?? accountCurrency;
  const navAgeDays = displayedSnapshot ? snapshotAgeDays(displayedSnapshot.asOfDate) : 0;

  if (me.isLoading || (accounts.isLoading && !primaryAccount)) {
    return <main className="p-6 text-text-muted">Loading dashboard...</main>;
  }

  if (me.isError || accounts.isError || !me.data) {
    return <main className="p-6 text-danger">Could not load dashboard.</main>;
  }

  return (
    <main className="min-h-screen bg-bg text-text">
      <BehaviouralFlagBanner />
      <TopStatusBar
        currency={accountCurrency}
        metrics={metrics.data}
        snapshot={displayedSnapshot}
      />
      <div className="mx-auto max-w-5xl space-y-6 px-4 py-6 sm:px-6">
        <PortfolioDisciplineCard
          accountId={primaryAccount?.id ?? null}
          currency={accountCurrency}
          latestSnapshot={
            latestSnapshot.data
              ? {
                  id: latestSnapshot.data.id,
                  nav: latestSnapshot.data.nav,
                  asOfDate: latestSnapshot.data.asOfDate,
                }
              : null
          }
        />
        {primaryAccount?.mandate === 'SWING_TRADING' ? (
          <TradingPortfolioHeatCard
            heat={tradingPortfolioHeat.data}
            isError={tradingPortfolioHeat.isError}
            isLoading={tradingPortfolioHeat.isLoading}
          />
        ) : null}
        {portfolioSummary.isLoading && primaryAccount ? (
          <PortfolioSummarySkeleton />
        ) : portfolioSummary.data ? (
          <PortfolioSummaryWidgets currency={accountCurrency} summary={portfolioSummary.data} />
        ) : null}
        {displayedSnapshot && navAgeDays >= 1 ? (
          <section className="flex items-start gap-3 rounded border border-warn/60 bg-warn/10 px-4 py-3 text-sm text-amber-100">
            <TriangleAlert className="mt-1 h-5 w-5 shrink-0 text-warn" aria-hidden="true" />
            <div>
              <Badge tone="warning">Stale NAV</Badge>
              <p className="mt-2">
                Your NAV is {navAgeDays} {navAgeDays === 1 ? 'day' : 'days'} old. Update it in
                Settings for accurate position sizing.
              </p>
            </div>
          </section>
        ) : null}
        <PendingReviewBanner />
        <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold">Dashboard</h1>
            <p className="mt-1 text-sm text-text-muted">
              {primaryAccount
                ? `${primaryAccount.name} | ${primaryAccount.broker} | ${primaryAccount.currency}`
                : 'Create an account before entering NAV.'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="secondary">
              <Link to="/positions">Positions</Link>
            </Button>
            <Button asChild variant="secondary">
              <Link to="/settings">Settings</Link>
            </Button>
          </div>
        </header>

        <KeyMetricsStrip currency={accountCurrency} metrics={metrics.data} />
        <MarketStanceCard />
        <BehaviouralFlagFeed />

        <div className="grid gap-6 lg:grid-cols-[1fr_18rem]">
          <DrawdownChart history={drawdownHistory.data} />
          <DrawdownGauge currency={accountCurrency} metrics={metrics.data} />
        </div>

        <PnlChart
          currency={accountCurrency}
          groupBy={pnlGroupBy}
          history={pnlHistory.data}
          onGroupByChange={setPnlGroupBy}
        />

        <Card
          header={
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold">Open positions</h2>
                <p className="text-sm text-text-muted">Top 5 by percent of NAV.</p>
              </div>
              {positions.data ? (
                <PricesAsOf
                  positions={positions.data.positions}
                  refreshing={positions.isFetching}
                />
              ) : null}
            </div>
          }
        >
          {positions.isLoading ? (
            <p className="text-sm text-text-muted">Loading positions...</p>
          ) : topPositions.length > 0 ? (
            <div className="space-y-3">
              {topPositions.map((position) => (
                <div
                  key={position.symbol}
                  className="grid gap-2 rounded border border-border bg-bg-muted p-3 sm:grid-cols-4"
                >
                  <div>
                    <div className="font-semibold">{position.symbol}</div>
                    <div className="text-sm text-text-muted">{position.theme ?? 'No theme'}</div>
                  </div>
                  <div className="tabular-nums">
                    <div className="text-sm text-text-muted">% NAV</div>
                    <div>{(Number(position.pctNav) * 100).toFixed(1)}%</div>
                  </div>
                  <div className="tabular-nums">
                    <div className="text-sm text-text-muted">Market Value</div>
                    <div>{formatCurrency(position.marketValue, position.currency)}</div>
                  </div>
                  <div className="tabular-nums">
                    <div className="text-sm text-text-muted">Risk to Stop</div>
                    <div>{formatCurrency(position.dollarRiskToStop, position.currency)}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-text-muted">No open positions for this account.</p>
          )}
        </Card>

        <Card
          header={
            <div>
              <h2 className="text-lg font-semibold">Latest snapshot</h2>
              <p className="text-sm text-text-muted">
                {displayedSnapshot
                  ? `Updated ${relativeTimestamp(displayedSnapshot.createdAt)}`
                  : "Enter today's NAV in Settings."}
              </p>
            </div>
          }
        >
          {latestSnapshot.isLoading && !latestSnapshot.data && primaryAccount ? (
            <p className="text-sm text-text-muted">Loading snapshot...</p>
          ) : displayedSnapshot ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <MetricCard
                label="NAV"
                value={formatCurrency(displayedSnapshot.nav, displayedCurrency)}
              />
              <MetricCard
                label="Cash"
                value={formatCurrency(displayedSnapshot.cash, displayedCurrency)}
              />
              <MetricCard
                label="Margin Used"
                value={formatCurrency(displayedSnapshot.marginUsed, displayedCurrency)}
              />
              <MetricCard
                label="Buying Power"
                value={formatCurrency(displayedSnapshot.buyingPower, displayedCurrency)}
                delta={`90d peak ${formatCurrency(
                  displayedSnapshot.peakNavTrailing90d,
                  displayedCurrency,
                )}`}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-text-muted">
                {primaryAccount
                  ? 'No snapshot has been recorded for this account.'
                  : 'No accounts exist yet.'}
              </p>
              <Button asChild variant="secondary">
                <Link to="/settings">Open settings</Link>
              </Button>
            </div>
          )}
        </Card>
      </div>
    </main>
  );
}
